import React, { useContext, useEffect, useMemo, useState } from 'react'
import moment from 'moment'
import styled from 'styled-components'
import { get } from 'lodash'
import { AppContext } from '../../Context'
import PageLayout from '../../components/PageLayout'
import { RecordContext } from './RecordPage'
import StyledSelector, { ObjectSelector } from '../../../common/StyledSelector'
import { deleteAttachment, deleteRecord, getRecords } from '../../services/record-services'
import DatePicker from '../../../common/DatePicker'
import { useHistory } from 'react-router'
import Checkbox from '../../../common/Checkbox'
import { Comment, Error } from '../../../common/Text'
import Stepper from '../../components/Stepper'
import { HoursTypes } from '../../../common/HoursType'
import LabelField from '../../../common/LabelField'
import { WarningConfirm } from '../../../common/Popup'
import { isActive } from '../../../common/Record'
import { getBabysittingModeFrom } from '../../../common/BabysittingModes'

const ChildInfoContainer = styled(PageLayout)`

  .eligible-children {
    margin-bottom: 5px;
  }

  .babysitting-modes, .dateExpired {
    margin-bottom: 20px;
  }

  .notice {
    margin-top: 5px;
    margin-bottom: 20px;
  }

  .date {
    font-weight: bold;
    font-size: 12px;
  }

  .checkbox-atypical-hours {
    margin-top: 35px;
  }

  .input-atypical-hours {
    display: none;
  }

  .label-atypical-hours {
    margin-right: 15px;
  }

  .notice-atypical-hours {
    margin-top: 10px;
  }

`

export default function ChildInfo(props) {

  const history = useHistory()

  const {
    employee,
    ageCategories,
    babysittingModes,
    flagRecordsUpdate,
    setFlagRecordsUpdate,
    recordAttachConfig
  } = useContext(AppContext)
  const { record, setRecord, setRecordValue, original, updateRecord, parentHistory, steps } = useContext(RecordContext)

  const [error, setError] = useState(null)
  const [canSave, setCanSave] = useState(false)
  const [selectableBabysittingModes, setSelectableBabysittingModes] = useState([])
  const [childrenList, setChildrenList] = useState([])
  const [records, setRecords] = useState(null)
  const [showCancelRecordPopup, setShowCancelRecordPopup] = useState(false)

  const selectedRecordId = useMemo(() => get(record, '_id', null), [record])
  const selectedChildId = useMemo(() => get(record, 'child.id', null), [record])
  const selectedChildAgeCategory = useMemo(() => get(record, 'child.ageCategory', null), [record])

  const expiresThisYear = moment(get(record, 'child.expired'), 'YYYYMMDD').year() === moment().year()
  const showExpirationDate = expiresThisYear && get(record, 'child.ageCategory') === ageCategories.AGE_0_3.key

  const atypicalConfig = useMemo(() => recordAttachConfig && recordAttachConfig.filter(config => config.category === 'atypical_parent1')[0], [recordAttachConfig])
  const atypicalHoursComment = useMemo(() => atypicalConfig && atypicalConfig.description, [atypicalConfig])

  const childAgeCategory = useMemo(() => record && ageCategories && ageCategories[get(record, 'child.ageCategory')], [record])
  const babysittingMode = useMemo(() => record && getBabysittingModeFrom(babysittingModes, get(record, 'babysittingMode', null)), [record])
  const canChooseClassicOrAtypical = useMemo(() => childAgeCategory && babysittingMode
                                                      && childAgeCategory.canBeClassicOrAtypicalHoursType
                                                      && babysittingMode.canBeClassicOrAtypicalHoursType,
                                                    [childAgeCategory, babysittingMode])

  useEffect(() => {
    if (original){
      if (original && original.child && original.child.id && original.babysittingMode) {
        setRecordValue('disableChildInfoInputs', true)
      }
    }
  }, [original])

  useEffect(() => {
    if (!employee || !employee.children) return
    const listChildren = get(employee, 'children', []).filter(c => {
      return c.ageCategory !== ageCategories.AGE_7_P.key
    }).map(c => {
      return {
        ...c,
        birthday: moment(c.dateOfBirth, 'YYYYMMDD')
      }
    })
    setChildrenList(listChildren)
  }, [employee])

  useEffect(() => {
    const fetchRecords = async () => {
      const result = await getRecords(fields)
      const records = result.data.getRecords
      setRecords(records)
    }
    if (babysittingModes && !records) {
      fetchRecords()
    }
  }, [babysittingModes])

  useEffect(() => {
    if (!selectedRecordId || !selectedChildId || !babysittingModes || !records) return
    // We only retain the babysitting modes that match the age category of the selected child :
    const babysittingModesForAgeCategory = selectedChildAgeCategory ? babysittingModes.filter(mode => mode.age.indexOf(selectedChildAgeCategory) > -1) : []
    // We need to identify the babysitting modes related to the existing folders for this child :
    const babysittingModeKeysAlreadyUserForThisChild = records.filter(currentRecord => currentRecord._id !== selectedRecordId
                                                                                      && currentRecord.child && currentRecord.child.id === selectedChildId
                                                                                      && currentRecord.babysittingMode && isActive(currentRecord))
                                                              .map(currentRecord => currentRecord.babysittingMode)
    // We need to exclude those babysitting modes already used to not suggest them again :
    const retainedModesForThisChild = babysittingModesForAgeCategory.map(mode => mode.key)
                                                                    .filter(modeKey => babysittingModeKeysAlreadyUserForThisChild.indexOf(modeKey) === -1)
    // Finally, we need to exclude the home babysitting mode when one of the employee child already associates a file with this mode :
    const indexHomeBabysittingMode = retainedModesForThisChild.indexOf('home');
    const isHomeBabysittingModeAlreadyRelatedToAnExistingFileInTheFamily = records.filter(currentRecord => currentRecord._id !== selectedRecordId
                                                                                                          && isActive(currentRecord)
                                                                                                          && currentRecord.babysittingMode === 'home').length > 0

    // if a child is more than 3 years old, the home babysitting mode is not available if he/she has a siblings age 0-3.
    const hasSiblingsAge0to3 = childrenList.filter(child=> child.ageCategory === ageCategories.AGE_0_3.key).length > 0
    const isOlderThan3 = selectedChildAgeCategory !== ageCategories.AGE_0_3.key
    const excludeHomeBabysitting = hasSiblingsAge0to3 && isOlderThan3

    if (indexHomeBabysittingMode > -1 && (isHomeBabysittingModeAlreadyRelatedToAnExistingFileInTheFamily || excludeHomeBabysitting)) {
      retainedModesForThisChild.splice(indexHomeBabysittingMode, 1)
    }
    // We need to define the list of babysitting modes to suggest to the user for this child :
    const modesToSuggest = babysittingModes.filter(mode => retainedModesForThisChild.indexOf(mode.key) > -1)
    // Define the list or babysitting modes the user can select in the combo :
    setSelectableBabysittingModes(modesToSuggest)
  }, [selectedRecordId, selectedChildId, babysittingModes, records])

  useEffect(() => {
    if (!record) return
    const er = getError(record)
    setError(er)
    const canSaveInputs = !er && record.child && record.child.id && record.startDate && record.babysittingMode
    setCanSave(canSaveInputs)
  }, [record])

  const save = async () => {
    const listKeys = []
    let updatedQuery = ''
    if(get(original, 'child.id') !== get(record, 'child.id') || original.babysittingMode !== record.babysittingMode){
      if (record && record.attachments) {
        record.attachments.forEach(item => {
          if(childAttachmentList.indexOf(item.category) > -1) {
            listKeys.push(item.key)
          }
        })
      }
      updatedQuery += `babysittingMode: "${record.babysittingMode}"`
      updatedQuery += `hoursType: "${record.hoursType}"`
      if(record.specialContract) {
        updatedQuery += `
          specialContract: ${ record.specialContract }
        `
      }
    }
    if( original.startDate !== record.startDate ) {
      updatedQuery += `
        startDate: "${record.startDate}"
      `
    }

    if(get(original, 'child.id') !== get(record, 'child.id')) {
      updatedQuery += `
        child: {
          id: "${ record.child.id }"
          ageCategory: "${ record.child.ageCategory }"
          expired: "${ record.child.expired }"
          firstName: "${ record.child.firstName }"
          lastName: "${ record.child.lastName }"
          dateOfBirth: "${ record.child.dateOfBirth }"
        }
      `
    }
    if(listKeys && listKeys.length > 0) {
      await deleteAttachment(record._id, listKeys)
    }
    if(updatedQuery && updatedQuery.length > 0) {
      const resp = await updateRecord(updatedQuery)
      if (resp && resp.errors) {
        setError(resp.errors[0].message)
        return true
      }
      setError('')
      setRecordValue('disableChildInfoInputs', true)
      setFlagRecordsUpdate(!flagRecordsUpdate)
    }
    return true
  }

  const saveAndNext = async () => {
    save()
    history.push('/employee-contact')
    return true
  }

  const saveAndExit = async () => {
    // First, we need to save the inputs :
    save()
    // Then, we need to forward to home :
    parentHistory.push('/')
    return true;
  }

  const onPrevious = () => {
    if(!original.child || !original.babysittingMode) {
      setShowCancelRecordPopup(true)
    } else {
      parentHistory.push('/')
    }
  }

  const onConfirmCancelRecord = async () => {
    await deleteRecord(original._id)
    parentHistory.push('/')
    return true
  }

  const getError = (record) => {
    if(record.child) {
      const expiredDate = moment(record.child.expired, 'YYYYMMDD')
      const startDate = moment(record.startDate, 'YYYYMMDD')
      if (startDate.isAfter(expiredDate)) {
        return 'La date de début du mode de garde est erronée, veuillez la corriger'
      }
      const rightStartDate = moment(record.child.dateOfBirth)
      if(record.child.ageCategory === ageCategories.AGE_0_3.key) {
        rightStartDate.add(2, 'M')
      } else {
        rightStartDate.add(3, 'year').month(7).date(31)
      }
      if ( rightStartDate.isAfter(startDate)) {
        return 'La date de début du mode de garde est erronée, veuillez la corriger'
      }
    }
  }

  return (
    <ChildInfoContainer
      title="1 | Mon enfant"
      onPrevious={ onPrevious }
      actions={ {
        submitTxt: 'Suivant',
        onSubmit: saveAndNext,
        disableSubmit: !canSave,
        onCancel: saveAndExit,
        cancelTxt: 'Enregistrer et revenir plus tard',
        // We can even save and exit unless every mandatory field has been defined correctly.
        disableCancel: !canSave,
      } }
    >
      <Stepper steps={steps} activeStep={0}/>
      <ObjectSelector
        label="Enfant éligible"
        options={ childrenList }
        value={ get(record, 'child.id') || '' }
        placeholder="Choisissez un enfant"
        disabled={ get(record, 'disableChildInfoInputs') }
        getKey={ (item) => item.id }
        getLabel={ (item) => `${ item.firstName } ${ item.lastName } ${ item.birthday.format('DD/MM/YYYY') }` }
        onValueChange={ (child) => {
            setRecord({
              ...record,
              child,
              // When selecting a child in the combo, the default hours type is classic (can be updated though).
              hoursType: HoursTypes.Classique.key,
              babysittingMode: null,
              specialContract: child.specialContract
            })
          }
        }
        className="eligible-children"
      />
      <Comment className='notice'>
        Si vous ne trouvez pas votre enfant, nous vous engageons à contacter votre gestion RH locale.
      </Comment>

      {
        showExpirationDate && <LabelField className={'dateExpired'}
          label={`Date de fin de droit à l'allocation : ${ moment(get(record, 'child.expired'), 'YYYYMMDD').format('DD MMM YYYY')}`}>
          <span>
            <Comment>
              Si cette date est incorrecte, veuillez supprimer votre demande et la refaire à partir du <span className="date">1er août</span>.
            </Comment>
          </span>
        </LabelField>
      }

      {
        babysittingModes &&
        <StyledSelector
          label="Mode de garde"
          options={ selectableBabysittingModes }
          getValue={ (mode) => mode.key }
          value={ get(record, 'babysittingMode') || '' }
          placeholder="Aucune"
          disabled={ get(record, 'disableChildInfoInputs') }
          onValueChange={ (value) => setRecordValue('babysittingMode', value) }
          className="babysitting-modes"
        />
      }

      <DatePicker
        label="Date de début de garde"
        placeholderText="jj / mm / aaaa"
        selected={ record && record.startDate ? moment(record.startDate, 'YYYYMMDD').toDate() : null }
        onChange={ (date) => {
          const d = moment(date).format('YYYYMMDD')
          setRecordValue('startDate', d)
        } }
        disabled={ get(record, 'disabled') }
        dateFormat="dd/MM/yyyy"
        className="date-picker"
        strictParsing
      />

      { canChooseClassicOrAtypical && <div>
          <Checkbox className="checkbox-atypical-hours" checkboxClassName="input-atypical-hours"
              labelClassName="label-atypical-hours"
              checked={ get(record, 'hoursType') === HoursTypes.Atypique.key }
              disabled={ get(record, 'disableChildInfoInputs') }
              onChange={ (value) => setRecordValue('hoursType', value === true ? HoursTypes.Atypique.key : HoursTypes.Classique.key ) }
              position="right">
                Ma garde s’effectue en horaires atypiques
          </Checkbox>
          <Comment className='notice-atypical-hours'>{ atypicalHoursComment }</Comment>
        </div>
      }

      { error && <Error> { error }</Error> }

      { showCancelRecordPopup && <WarningConfirm
        fullscreen={true}
        onCancel={() => setShowCancelRecordPopup(false)}
        onConfirm={onConfirmCancelRecord}
        isOpen={showCancelRecordPopup}
        actionsOptions={{
          submitTxt: 'Oui',
          cancelTxt: 'Non',
          submitButtonClass: 'inverse',
          cancelButtonClass: 'normal'
        }}
      >
        <div className="bold">Vous êtes sur le point d’annuler votre demande de dépôt de dossier.</div>
        <div>Voulez vous continuer ?</div>
      </WarningConfirm>}
    </ChildInfoContainer>
  )
}

const fields = `
  _id
  babysittingMode
  status
  child {
    id
  }
`
const childAttachmentList = [
  'babysitting',
  'babysitting_supp',
  'atypical_parent1',
  'atypical_parent2'
]

